import React from 'react';
import styled from 'styled-components';
import { Workflow, Loader, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useQuery } from 'react-query';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const TableContainer = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: ${props => props.theme.colors.background};
`;

const Th = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Badge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => props.bg || props.theme.colors.background};
  color: ${props => props.color || props.theme.colors.text};
`;

const LoadingState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

function Jobs() {
  const { data, isLoading, error } = useQuery(
    'jobs',
    () => adminAPI.getJobs(),
    {
      refetchInterval: 30000,
      onError: (err) => {
        console.error('Jobs error:', err);
      }
    }
  );

  const getStatusBadge = (status) => {
    const statusConfig = {
      queued: { bg: '#dbeafe', color: '#2563eb', icon: Clock },
      running: { bg: '#dbeafe', color: '#2563eb', icon: Loader },
      completed: { bg: '#d1fae5', color: '#059669', icon: CheckCircle },
      failed: { bg: '#fee2e2', color: '#dc2626', icon: XCircle },
      canceled: { bg: '#f3f4f6', color: '#6b7280', icon: XCircle },
    };
    const config = statusConfig[status] || { bg: '#f3f4f6', color: '#6b7280', icon: Clock };
    const Icon = config.icon;
    return (
      <Badge bg={config.bg} color={config.color}>
        <Icon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
        {status}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ru-RU');
  };

  return (
    <Container>
      <Header>
        <Title>
          <Workflow size={32} />
          Задачи
        </Title>
      </Header>

      {isLoading ? (
        <LoadingState>Загрузка задач...</LoadingState>
      ) : error ? (
        <EmptyState>Ошибка загрузки данных</EmptyState>
      ) : !data || !data.data || !data.data.jobs || data.data.jobs.length === 0 ? (
        <EmptyState>Задач не найдено</EmptyState>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <Th>ID</Th>
                  <Th>User ID</Th>
                  <Th>Provider</Th>
                  <Th>Тип</Th>
                  <Th>Prompt</Th>
                  <Th>Статус</Th>
                  <Th>Стоимость</Th>
                  <Th>Создано</Th>
                  <Th>Завершено</Th>
                </tr>
              </TableHeader>
              <tbody>
                {data.data.jobs.map((job) => (
                  <tr key={job.id}>
                    <Td>{job.id}</Td>
                    <Td>{job.user_id}</Td>
                    <Td>{job.provider_id}</Td>
                    <Td>
                      <Badge bg="#e0e7ff" color="#4f46e5">{job.type}</Badge>
                    </Td>
                    <Td title={job.prompt || '-'}>{job.prompt || '-'}</Td>
                    <Td>{getStatusBadge(job.status)}</Td>
                    <Td>{job.cost_credits || 0} кр.</Td>
                    <Td>{formatDate(job.created_at)}</Td>
                    <Td>{formatDate(job.finished_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <div style={{ marginTop: '16px', textAlign: 'center', color: '#6b7280' }}>
            Всего: {data.data.total || 0}
          </div>
        </>
      )}
    </Container>
  );
}

export default Jobs;
